import { type Action, type ListenerEffectAPI, type PayloadAction, TaskAbortError } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { workspaceApi } from '@/api/workspace/index.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { selectCashPriceStatus } from '@/store/cash/selectors/prices.ts';
import { cashSpotTieringError, cashSpotTieringUpdated } from '@/store/cash/slice.ts';
import { espApi } from '@/store/esp/api';
import { espStreamSubscribed, espStreamUnsubscribed } from '@/store/esp/slice.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { addAppListener, type AppListenerEffect } from '@/store/storeMethods.ts';
import { addTile, duplicateTile, removeTile, updateTileProductType } from '@/store/tiles/slice.ts';

export const registerCashEspListeners = (startAppListening: StartAppListening) => {
  // subscribe to ESP when a tile is added
  startAppListening({
    actionCreator: addTile,
    effect: effectOfAddTileActionOnESP,
  });

  startAppListening({
    actionCreator: duplicateTile,
    effect: effectOfDuplicateTileActionOnESP,
  });

  startAppListening({
    actionCreator: updateTileProductType,
    effect: effectOfUpdateTileProductTypeActionOnESP,
  });

  // subscribe to ESP when the workspace is loaded
  startAppListening({
    matcher: workspaceApi.endpoints.getWorkspace.matchFulfilled,
    effect: async (action, api) => {
      action.payload?.products.cash.forEach((cash) => {
        effectOfAddTileActionOnESP({ payload: { tile: { tileId: cash.tileId } }, type: action.type }, api);
      });
    },
  });

  startAppListening({
    matcher: workspaceApi.endpoints.getSharedTab.matchFulfilled,
    effect: (action, api) => {
      action.payload?.tiles.forEach((tile) => {
        effectOfAddTileActionOnESP({ payload: { tile: { tileId: tile.tileId } }, type: action.type }, api);
      });
    },
  });
};

const unsubscribeCondition = (tileId: string) => (action: Action) => {
  return (
    (removeTile.match(action) && action.payload === tileId) ||
    (updateTileProductType.match(action) && action.payload.tileId === tileId)
  );
};

// ██████╗ ███████╗██╗   ██╗███████╗ █████╗ ██████╗ ██╗     ███████╗    ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
// ██╔══██╗██╔════╝██║   ██║██╔════╝██╔══██╗██╔══██╗██║     ██╔════╝    ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
// ██████╔╝█████╗  ██║   ██║███████╗███████║██████╔╝██║     █████╗      █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
// ██╔══██╗██╔══╝  ██║   ██║╚════██║██╔══██║██╔══██╗██║     ██╔══╝      ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
// ██║  ██║███████╗╚██████╔╝███████║██║  ██║██████╔╝███████╗███████╗    ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
// ╚═╝  ╚═╝╚══════╝ ╚═════╝ ╚══════╝╚═╝  ╚═╝╚═════╝ ╚══════╝╚══════╝    ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

// The processed action must have these fields on order to be listened by the effectOfAddTileActionOnESP function
type AddTileEffectOnESPAction = PayloadAction<{
  tile: { tileId: string };
}>;

export const effectOfAddTileActionOnESP = async (
  action: AddTileEffectOnESPAction,
  api: ListenerEffectAPI<AppState, AppDispatch>,
) => {
  const {
    tile: { tileId },
  } = action.payload;

  // listen any events which can require to subscribe to ESP
  // or unsubscribe from it
  const allEffectsAfterTileChanged = [
    tieringEffect(tileId),
    espSubscriptionEffect(tileId),
    espUnsubscriptionEffect(tileId),
  ];

  const unsubscribe = api.dispatch(
    addAppListener({
      predicate: (_action, currentState, previousState) => {
        return isEspCompatibleCashTileChanged(tileId, previousState, currentState);
      },

      effect: (action, api) => {
        for (const effect of allEffectsAfterTileChanged) {
          effect(action, api);
        }
      },
    }),
  );

  try {
    if (await api.condition(unsubscribeCondition(tileId))) {
      unsubscribe();
    }
  } catch (error) {
    if (!(error instanceof TaskAbortError)) {
      logger.logError('Unexpected error in effectOfAddTileActionOnESP:', error);
    }
  }
};

export const effectOfDuplicateTileActionOnESP = async (
  action: ReturnType<typeof duplicateTile>,
  api: ListenerEffectAPI<AppState, AppDispatch>,
) => {
  const { newTileId: tileId } = action.payload;

  // listen any events which can require to subscribe to ESP
  // or unsubscribe from it
  const allEffectsAfterTileChanged = [
    tieringEffect(tileId),
    espSubscriptionEffect(tileId),
    espUnsubscriptionEffect(tileId),
  ];

  const unsubscribe = api.dispatch(
    addAppListener({
      predicate: (_action, currentState, previousState) => {
        return isEspCompatibleCashTileChanged(tileId, previousState, currentState);
      },

      effect: (action, api) => {
        for (const effect of allEffectsAfterTileChanged) {
          effect(action, api);
        }
      },
    }),
  );

  try {
    await api.condition(unsubscribeCondition(tileId));
  } catch (_error) {
    // TaskAbortError is expected when the listener is cancelled
    // This happens when the tile is removed or product type is changed
    // No need to log this error as it's part of normal cleanup
  } finally {
    // Always unsubscribe to cleanup the listener, regardless of how we exit
    unsubscribe();
  }
};

export const effectOfUpdateTileProductTypeActionOnESP = async (
  action: ReturnType<typeof updateTileProductType>,
  api: ListenerEffectAPI<AppState, AppDispatch>,
) => {
  const { tileId } = action.payload;

  // reuse the same logic as for addTile action
  await effectOfAddTileActionOnESP(
    {
      payload: { tile: { tileId } },
      type: action.type,
    },
    api,
  );
};

const isEspCompatibleCashTileChanged = (tileId: string, previousState: AppState, currentState: AppState) => {
  const previousTile = previousState.cash[tileId];
  const currentTile = currentState.cash[tileId];

  return (
    currentTile !== previousTile &&
    (currentTile?.type === 'FxSpot' ||
      currentTile?.type === 'FxNdf' ||
      previousTile?.type === 'FxSpot' ||
      previousTile?.type === 'FxNdf')
  );
};

// ████████╗██╗███████╗██████╗ ██╗███╗   ██╗ ██████╗
// ╚══██╔══╝██║██╔════╝██╔══██╗██║████╗  ██║██╔════╝
//    ██║   ██║█████╗  ██████╔╝██║██╔██╗ ██║██║  ███╗
//    ██║   ██║██╔══╝  ██╔══██╗██║██║╚██╗██║██║   ██║
//    ██║   ██║███████╗██║  ██║██║██║ ╚████║╚██████╔╝
//    ╚═╝   ╚═╝╚══════╝╚═╝  ╚═╝╚═╝╚═╝  ╚═══╝ ╚═════╝

const tieringEffect =
  (tileId: string): AppListenerEffect =>
  async (_action, api) => {
    const priceStatus = selectCashPriceStatus(api.getState(), tileId);

    if (priceStatus.status !== 'esp-tiering-required') {
      return;
    }

    const result = await api.dispatch(
      espApi.endpoints.getEspTiering.initiate({
        clientId: priceStatus.clientId,
        currencyPair: priceStatus.currencyPair,
        spotDate: priceStatus.maturityDate,
      }),
    );

    if (result.error) {
      api.dispatch(cashSpotTieringError({ tileId }));
      return;
    }

    if ('data' in result && isDefined(result.data) && !('error' in result.data)) {
      api.dispatch(
        cashSpotTieringUpdated({
          tileId,
          tiering: result.data.tiering,
          isDefaultTiering: result.data.isDefaultTiering,
        }),
      );
    }
  };

// ███████╗███████╗██████╗     ███████╗██╗   ██╗██████╗ ███████╗ ██████╗██████╗ ██╗██████╗ ████████╗██╗ ██████╗ ███╗   ██╗
// ██╔════╝██╔════╝██╔══██╗    ██╔════╝██║   ██║██╔══██╗██╔════╝██╔════╝██╔══██╗██║██╔══██╗╚══██╔══╝██║██╔═══██╗████╗  ██║
// █████╗  ███████╗██████╔╝    ███████╗██║   ██║██████╔╝███████╗██║     ██████╔╝██║██████╔╝   ██║   ██║██║   ██║██╔██╗ ██║
// ██╔══╝  ╚════██║██╔═══╝     ╚════██║██║   ██║██╔══██╗╚════██║██║     ██╔══██╗██║██╔═══╝    ██║   ██║██║   ██║██║╚██╗██║
// ███████╗███████║██║         ███████║╚██████╔╝██████╔╝███████║╚██████╗██║  ██║██║██║        ██║   ██║╚██████╔╝██║ ╚████║
// ╚══════╝╚══════╝╚═╝         ╚══════╝ ╚═════╝ ╚═════╝ ╚══════╝ ╚═════╝╚═╝  ╚═╝╚═╝╚═╝        ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝

// the behavior is defined in
// fx-web/app/src/state/esp/espStreamsSelectors.ts
const espSubscriptionEffect =
  (tileId: string): AppListenerEffect =>
  (_action, api) => {
    const priceStatus = selectCashPriceStatus(api.getState(), tileId);

    if (priceStatus.status !== 'esp-required') {
      return;
    }

    api.dispatch(
      espStreamSubscribed({
        tileId,
        currencyPair: priceStatus.currencyPair,
        tiering: priceStatus.tiering,
        bdrId: priceStatus.clientId,
      }),
    );
  };

// ███████╗███████╗██████╗     ██╗   ██╗███╗   ██╗███████╗██╗   ██╗██████╗ ███████╗ ██████╗██████╗ ██╗██████╗ ████████╗██╗ ██████╗ ███╗   ██╗
// ██╔════╝██╔════╝██╔══██╗    ██║   ██║████╗  ██║██╔════╝██║   ██║██╔══██╗██╔════╝██╔════╝██╔══██╗██║██╔══██╗╚══██╔══╝██║██╔═══██╗████╗  ██║
// █████╗  ███████╗██████╔╝    ██║   ██║██╔██╗ ██║███████╗██║   ██║██████╔╝███████╗██║     ██████╔╝██║██████╔╝   ██║   ██║██║   ██║██╔██╗ ██║
// ██╔══╝  ╚════██║██╔═══╝     ██║   ██║██║╚██╗██║╚════██║██║   ██║██╔══██╗╚════██║██║     ██╔══██╗██║██╔═══╝    ██║   ██║██║   ██║██║╚██╗██║
// ███████╗███████║██║         ╚██████╔╝██║ ╚████║███████║╚██████╔╝██████╔╝███████║╚██████╗██║  ██║██║██║        ██║   ██║╚██████╔╝██║ ╚████║
// ╚══════╝╚══════╝╚═╝          ╚═════╝ ╚═╝  ╚═══╝╚══════╝ ╚═════╝ ╚═════╝ ╚══════╝ ╚═════╝╚═╝  ╚═╝╚═╝╚═╝        ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝

const espUnsubscriptionEffect =
  (tileId: string): AppListenerEffect =>
  (_action, api) => {
    const state = api.getState();

    // an ESP stream is already subscribed to this tile ?
    const currentTileEspId = Object.entries(state.esp.streams).find(([_espId, stream]) => {
      return stream.subscribedTileIds.includes(tileId);
    });

    if (isNotDefined(currentTileEspId)) {
      return;
    }

    // the price status of the tile is still compatible with ESP subscription ?
    const priceStatus = selectCashPriceStatus(state, tileId);

    if (
      priceStatus.status === 'esp-required' ||
      priceStatus.status === 'esp-pending' ||
      priceStatus.status === 'esp-price' ||
      ((priceStatus.status === 'rfs-required' ||
        priceStatus.status === 'rfs-pending' ||
        priceStatus.status === 'rfs-price') &&
        priceStatus.keepEsp)
    ) {
      return;
    }

    // the tile is not compatible with ESP subscription anymore
    const [currencyPair, tiering] = currentTileEspId[0].split('&&');

    if (isNotDefined(currencyPair) || isNotDefined(tiering)) {
      return;
    }

    api.dispatch(
      espStreamUnsubscribed({
        tileId,
        currencyPair,
        tiering,
      }),
    );
  };
